import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Stack,
    Typography,
    Chip,
} from "@mui/material";
import { ref, onValue, update } from "firebase/database";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { database } from "../services/firebase";

const DashboardUpdatesModal = () => {
    const [open, setOpen] = useState(false);
    const [updates, setUpdates] = useState([]);
    const [checkedMap, setCheckedMap] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const updatesRef = ref(database, "dashboardUpdates");

        const unsubscribe = onValue(updatesRef, (snap) => {
            const list = [];

            if (snap.exists()) {
                Object.entries(snap.val()).forEach(([id, item]) => {
                    if (item?.active === true && item?.viewed !== true) {
                        list.push({
                            id,
                            ...item,
                        });
                    }
                });
            }

            list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setUpdates(list);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        setOpen(updates.length > 0);
    }, [updates]);

    useEffect(() => {
        const nextChecked = {};
        updates.forEach((item) => {
            nextChecked[item.id] = false;
        });
        setCheckedMap(nextChecked);
    }, [updates]);

    const formatDateTime = (timestamp) => {
        if (!timestamp) return { date: "-", time: "-" };

        const dateObj = new Date(timestamp);

        return {
            date: format(dateObj, "dd/MM/yyyy", { locale: ptBR }),
            time: format(dateObj, "HH:mm", { locale: ptBR }),
        };
    };

    const handleToggleCheck = (id) => {
        setCheckedMap((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const allChecked =
        updates.length > 0 &&
        updates.every((item) => checkedMap[item.id] === true);

    const handleConfirm = async () => {
        if (updates.length === 0) return;

        try {
            setSaving(true);

            const now = Date.now();

            await Promise.all(
                updates.map((item) =>
                    update(ref(database, `dashboardUpdates/${item.id}`), {
                        viewed: true,
                        viewedAt: now,
                    })
                )
            );

            setOpen(false);
        } catch (error) {
            console.error("Erro ao salvar visualização das atualizações:", error);
        } finally {
            setSaving(false);
        }
    };

    if (updates.length === 0) return null;

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown
        >
            <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>
                Histórico de Atualizações
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Alert severity="info" sx={{ fontWeight: 600 }}>
                        Leia as atualizações abaixo e marque que visualizou para continuar.
                    </Alert>

                    {updates.map((item, index) => {
                        const { date, time } = formatDateTime(item.createdAt);

                        return (
                            <Box
                                key={item.id}
                                sx={{
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    borderRadius: 3,
                                    p: 2.5,
                                    background: "#fff",
                                }}
                            >
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    spacing={1}
                                    sx={{ mb: 1.5 }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 800, color: "var(--azulEscuro)" }}
                                    >
                                        {item.title || "Atualização"}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <Chip label={date} size="small" sx={{ fontWeight: 700 }} />
                                        <Chip label={time} size="small" sx={{ fontWeight: 700 }} />
                                    </Stack>
                                </Stack>

                                <Typography
                                    sx={{
                                        color: "var(--cinza)",
                                        lineHeight: 1.7,
                                        whiteSpace: "pre-line",
                                    }}
                                >
                                    {item.description || "-"}
                                </Typography>

                                <FormControlLabel
                                    sx={{ mt: 2 }}
                                    control={
                                        <Checkbox
                                            checked={!!checkedMap[item.id]}
                                            onChange={() => handleToggleCheck(item.id)}
                                        />
                                    }
                                    label="Li e visualizei esta atualização"
                                />

                                {index < updates.length - 1 && <Divider sx={{ mt: 2 }} />}
                            </Box>
                        );
                    })}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    variant="contained"
                    disabled={!allChecked || saving}
                    onClick={handleConfirm}
                    sx={{
                        bgcolor: "var(--azulEscuro)",
                        borderRadius: 2,
                        fontWeight: 700,
                    }}
                >
                    {saving ? "Salvando..." : "Continuar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardUpdatesModal;